import { useEffect, useState } from 'react';
import {
  driver,
  masonrySetTitleBaseActionHandler,
} from '@leagueplatform/masonry';

// Reuse the SDK's own set-title handler type so the interceptor stays assignable
// to overrideHandler (and to the default handler restored on unmount).
type SetTitleHandler = typeof masonrySetTitleBaseActionHandler;

/**
 * Returns the server-driven screen title for the Plan Switcher gate — the value
 * of the picker's `setTitle` action — or `null` until it fires.
 *
 * Only the multi-plan picker emits `setTitle` ("Your plans"); the single-plan
 * gate's loader and the initial async spinner deliberately do not. The gate uses
 * this as the reliable, server-driven signal for "the picker is showing" so it
 * reveals the bordered card (with the title inside it, per design) ONLY for
 * genuinely multi-plan members. Single-plan and loading stay a bare spinner, so
 * the card never flashes for members who are auto-resolved past the switcher.
 * This is why the signal is `setTitle` and not a client-side plan count, which
 * is only persisted after the gate has already run and so cannot gate the gate.
 *
 * While mounted it intercepts the `setTitle` action on the shared driver to
 * capture the title, then falls through to the SDK's default handler so the
 * document title still updates. The override is global (the driver's action
 * controller is a module singleton), so it is installed only for the gate's
 * lifetime and the original handler is restored on unmount — mirroring
 * usePlanSwitcherGateCompletion. Mounting two gates at once is not supported.
 */
export const usePlanSwitcherGateTitle = (): string | null => {
  const [title, setTitle] = useState<string | null>(null);

  useEffect(() => {
    const interceptingHandler: SetTitleHandler = (action, resources) => {
      const nextTitle = action.payload?.title;
      if (typeof nextTitle === 'string' && nextTitle.length > 0) {
        setTitle(nextTitle);
      }
      return masonrySetTitleBaseActionHandler(action, resources);
    };

    driver.actionController.overrideHandler(
      'setTitle',
      'default',
      interceptingHandler,
    );

    return () => {
      driver.actionController.overrideHandler(
        'setTitle',
        'default',
        masonrySetTitleBaseActionHandler,
      );
    };
  }, []);

  return title;
};
